import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { capitalize } from '@attain-apps/string-utils'
import { ComponentStore } from '@ngrx/component-store'

import * as state from '../../../../../../../state'

import { State } from './state'

// TODO: maybe this is complicated enough to warrant a global state feature

/**
 * Produces a string that conveys where the action is being dispatched from
 */
function _getActionSource(...parts: readonly string[]): string {
  return `'quiver.deployment-center.Store.${parts.join('.')}`
}

/**
 * Used to manage the UI around deploying Quiver resources
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Store extends ComponentStore<State> {
  /**
   * Represents what stage in the deployment process the user is at
   *
   * @see {@link State.stage}
   */
  public readonly stageSg = this.selectSignal(_state => _state.stage)

  /**
   * Human-readable version of {@link stageSg}
   */
  public readonly stageNameSg = ngCore.computed(
    (stage = this.stageSg()) => stage && capitalize(stage),
  )

  /**
   * Indicates {@link previous} can be called
   */
  public readonly hasPreviousSg = ngCore.computed(
    (stage = this.stageSg()) => !!stage && stage !== 'packages',
  )

  /**
   * Indicates {@link next} can be called
   */
  public readonly hasNextSg = ngCore.computed(
    (
      stage = this.stageSg(),
      evaluationIsComplete = this.evaluationIsCompleteSg(),
    ) => !!stage && !evaluationIsComplete,
  )

  /**
   * Indicates {@link submit} can be called
   */
  public readonly canSubmitSg = ngCore.computed(
    (
      evaluationIsComplete = this.evaluationIsCompleteSg(),
      isDeploying = this._evaluationFeature.selectors.isDeploying.asSignal(),
    ) => evaluationIsComplete && !isDeploying,
  )

  /**
   * Indicates there are no more steps in the evaluation
   */
  public readonly evaluationIsCompleteSg = ngCore.computed(
    (
      evaluation = this._evaluationFeature.selectors.currentEvaluation.asSignal(),
    ) => !!evaluation?.commands,
  )

  /** @see {@link state.quiver.packages.Collection} */
  private readonly _packagesEc = ngCore.inject(state.quiver.packages.Collection)

  /** @see {@link state.quiver.resources.Collection} */
  private readonly _resourcesEc = ngCore.inject(
    state.quiver.resources.Collection,
  )

  /** @see {@link state.quiver.evaluation.Feature} */
  private readonly _evaluationFeature = ngCore.inject(
    state.quiver.evaluation.Feature,
  )

  constructor() {
    super(State.INITIAL)
  }

  /**
   * Starts the deployment process
   */
  public start(): void {
    this.setState({ stage: 'packages' })
  }

  /**
   * Stops the deployment entirely
   */
  public cancel(): void {
    const source = _getActionSource('cancel()')
    this._resetState(source)
    // Clears any evaluations
    this._evaluationFeature.actions.clearEvaluations.from(source).dispatch()
  }

  /**
   * Goes to the next step in the deployment
   *
   * __NOTE:__ this does not always result in switching stages
   */
  public next(): void {
    const stage = this.stageSg()
    if (!stage) {
      console.warn('Cannot call next() when stage is undefined')
      return
    }
    const actionSource = _getActionSource(`on-${stage}`, 'next()')
    switch (stage) {
      case 'packages': {
        this._selectResourcesBasedOnPackages(actionSource)
        // Proceeds to the resource selection
        this.patchState({ stage: 'resources' })
        break
      }
      case 'resources':
        this._evaluationFeature.actions.evaluate.from(actionSource).dispatch()
        this.patchState({ stage: 'evaluation' })
        break
      case 'evaluation':
        this._evaluationFeature.actions.evaluate.from(actionSource).dispatch()
        break
      default:
        // NOTE: `satisfies never` is similar to using
        // TypeAsserter.assertIsNever but we want to log a warning not throw an
        // error
        console.warn(`Unknown stage: ${stage satisfies never as string}`)
        break
    }
  }

  /**
   * Goes to the previous step in the deployment
   *
   * __NOTE:__ this does not always result in switching stages
   */
  public previous(): void {
    const stage = this.stageSg()
    if (!stage || stage === 'packages') {
      console.warn('Cannot call previous() when stage is undefined')
      return
    }
    const actionSource = _getActionSource(`on-${stage}`, 'previous()')
    switch (stage) {
      case 'resources':
        this.patchState({ stage: 'packages' })
        break
      case 'evaluation': {
        const evaluationCount =
          this._evaluationFeature.selectors.evaluationCount.asSignal()
        // Undoes the evaluation
        this._evaluationFeature.actions.undoLastEvaluation
          .from(actionSource)
          .dispatch()
        // NOTE: we don't expect less than 1, but better safe than sorry
        if (evaluationCount <= 1) {
          // If that was the only evaluation, then we no longer have any, so we
          // should go back to the resources list
          this.patchState({ stage: 'resources' })
        }
        break
      }
      default:
        // NOTE: `satisfies never` is similar to using
        // TypeAsserter.assertIsNever but we want to log a warning not throw an
        // error
        console.warn(`Unknown stage: ${stage satisfies never as string}`)
        break
    }
  }

  /**
   * Submits the deployment to actually be executed
   */
  public async submit(): Promise<void> {
    const source = _getActionSource('submit()')
    await this._evaluationFeature.actions.deploy.from(source).execute()
      .asPromise
    // If the evaluation completes successfully, then we can close the modal
    this._resetState(source)
  }

  /**
   * Resets to {@link State.INITIAL}
   */
  private _resetState(source: string): void {
    this._resourcesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from(source)
      .dispatch({ value: { release: true } })
    this._packagesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from(source)
      .dispatch({ value: { release: true } })
    this.setState(State.INITIAL)
  }

  /**
   * Sets the selected resources based on the selected packages
   */
  private _selectResourcesBasedOnPackages(source: string): void {
    // TODO: maybe we should compare to previously selected package IDs so
    // that we only add/remove resources based on what has changed, so that
    // we don't override manual resource selections/unselections unless a
    // related package has changed

    // NOTE: these should be valid IDs since they come from entities that
    // are pulled directly from entities
    const selectedPackageIds =
      this._packagesEc.plugins.selectionPlugin.feature.selectors
        .getAllSelectedIds()
        .asSignal() satisfies readonly string[] as readonly ents.extra.quiver.packages.Id[]
    // Sets the selected resources to be all of the resources in the selected packages
    this._resourcesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from(source)
      .dispatch({
        value: {
          set: ents.extra.quiver.getAllResourceIdsIn(...selectedPackageIds),
        },
      })
  }
}
